import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Spin, Tag } from 'antd';

import { friendlyError } from '../lib/api';
import { runAiJob } from '../lib/ai-job';
import { aiApi, type AiRewriteDto } from '../lib/wikiApi';

/** Частые просьбы — одним кликом в поле. */
const PRESETS = [
  'Оформи красиво в стиле базы знаний: разделы, выноски, таблицы',
  'Исправь ошибки и опечатки, больше ничего не меняй',
  'Сократи и упрости, сохранив все факты',
  'Добавь в конце чек-лист для проверки',
  'Разбей на пошаговую инструкцию',
];

const POLL_MS = 2500;
/** Страховка: потерянную задачу (перезапуск сервера) ловит пульс на бэкенде и отвечает `code: 'lost'`. */
const GIVE_UP_MS = 20 * 60_000;

/**
 * «ИИ: править статью» (05.10.2026; раньше — «Переписать по стайлгайду»):
 * своя просьба к статье или переписать по стайлгайду. Берёт текст прямо из
 * редактора (несохранённые правки тоже), показывает разницу; в редактор
 * попадает только по «Применить», сохранение — «Сохранить изменения».
 * Запрос — фоновая задача на бэке: ответ Claude на длинную статью идёт
 * дольше минуты, поэтому опрашиваем статус.
 */
export function AiRewriteModal({ slug, open, onClose, onApply, getCurrentMd }: {
  slug: string;
  open: boolean;
  onClose: () => void;
  onApply: (md: string) => void;
  /** Текущий текст редактора в Markdown. */
  getCurrentMd?: () => string;
}): JSX.Element {
  const [instruction, setInstruction] = useState('');
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<AiRewriteDto | null>(null);
  const runId = useRef(0);

  // Закрыли окно — бросаем опрос и очищаем результат.
  useEffect(() => {
    if (open) return;
    runId.current += 1;
    setRunning(false);
    setResult(null);
    setError(null);
    setNotice(null);
  }, [open]);

  const run = async (opts: { styleOnly?: boolean; refine?: boolean }): Promise<void> => {
    const my = ++runId.current;
    const base = opts.refine && result ? result.rewrittenMd : getCurrentMd?.();
    setRunning(true);
    setError(null);
    setNotice(null);
    setElapsed(0);
    try {
      const out = await runAiJob({
        start: () => aiApi.startEdit({ slug, bodyMd: base, instruction: opts.styleOnly ? undefined : instruction.trim() || undefined }),
        status: (jobId) => aiApi.editStatus(jobId),
        alive: () => runId.current === my,
        pollMs: POLL_MS,
        giveUpMs: GIVE_UP_MS,
        giveUpText: 'ИИ не ответил за 20 минут — попробуйте ещё раз или разбейте статью',
        onTick: ({ elapsedSec }) => setElapsed(elapsedSec),
        // Сервер перезапускался и потерял задачу — запускаем заново один раз.
        onRestart: () => setNotice('Сервер обновлялся — запустили заново'),
      });
      if (out.kind === 'done') setResult(out.result);
      else if (out.kind === 'error') setError(out.error);
    } catch (e) {
      if (runId.current === my) setError(friendlyError(e));
    } finally {
      if (runId.current === my) {
        setRunning(false);
        setNotice(null);
      }
    }
  };

  const d = result;
  const canApply = !!d && d.aiUsed && d.changes.length > 0 && !running;
  return (
    <Modal
      title="ИИ: править статью"
      open={open}
      onCancel={onClose}
      okText="Применить в редакторе"
      cancelText="Закрыть"
      okButtonProps={{ disabled: !canApply }}
      onOk={() => { if (d) { onApply(d.rewrittenMd); onClose(); } }}
      width={900}
      destroyOnHidden
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <Input.TextArea
          id="ai-edit-instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Что сделать со статьёй? Например: «Оформи красиво, добавь таблицу сроков и чек-лист в конце»"
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
          disabled={running}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PRESETS.map((p) => (
            <Tag key={p} style={{ cursor: running ? 'default' : 'pointer', marginInlineEnd: 0 }} onClick={() => { if (!running) setInstruction(p); }}>{p}</Tag>
          ))}
        </div>
        <Space wrap>
          <Button type="primary" loading={running} disabled={!instruction.trim()} onClick={() => void run({})}>Сделать</Button>
          <Button disabled={running} onClick={() => void run({ styleOnly: true })}>Переписать по стайлгайду</Button>
          {d?.aiUsed && <Button disabled={running || !instruction.trim()} onClick={() => void run({ refine: true })} title="Применить новую просьбу к варианту ниже, а не к тексту редактора">Доработать этот вариант</Button>}
        </Space>

        {running && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-muted)', fontSize: 13 }}>
            <Spin size="small" /> ИИ работает… {elapsed} с. Длинные статьи — до 2–3 минут, окно можно не трогать.
          </div>
        )}
        {running && notice && <Alert type="info" showIcon message={notice} />}
        {error && <Alert type="error" showIcon message={error} />}
        {d && !running && (
          <>
            {d.note && <Alert type="warning" showIcon message={d.note} />}
            {d.aiUsed && d.changes.length === 0 && <Alert type="success" showIcon message="ИИ не нашёл, что поменять" />}
            {d.changes.length > 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Изменено строк: {d.summary.modified} · добавлено {d.summary.added} · удалено {d.summary.deleted}. Ничего не сохранится, пока вы не нажмёте «Применить», а затем «Сохранить изменения».
              </div>
            )}
            <div style={{ maxHeight: 440, overflow: 'auto', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, border: '1px solid var(--border, #e8e6f0)', borderRadius: 8 }}>
              {d.changes.map((c, i) => (
                <div key={i} style={{ padding: '2px 8px', whiteSpace: 'pre-wrap', background: c.type === 'ADD' ? 'rgba(67,160,71,.10)' : c.type === 'DEL' ? 'rgba(229,57,53,.10)' : 'rgba(255,105,0,.08)' }}>
                  {c.type === 'MOD' && c.textOld != null && <div style={{ color: '#C62828', textDecoration: 'line-through' }}>{c.textOld}</div>}
                  <div style={{ color: c.type === 'DEL' ? '#C62828' : c.type === 'ADD' ? '#2E7D32' : undefined }}>{c.text}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
